import { DocxImportError } from '../../../../lib/docx/errors';

/*
 * Inserts imported HTML as one undo step without merging it into the block
 * under the caret. model.insertContent() merges the first and last inserted
 * blocks into a block it is inserted *inside*, which would strip their
 * alignment, indent and styles, so content always goes in *between* blocks.
 */

const isLimit = (el) => el.is('rootElement') || el.is('element', 'tableCell');

/** The selection's ancestor that is a direct child of the root or a table cell. */
function topBlock(position) {
    let node = position.parent;
    while (node && !node.is('rootElement') && node.parent && !isLimit(node.parent)) node = node.parent;
    return node;
}

/** After the last block of the list that `block` belongs to. */
function afterList(model, block) {
    let last = block;
    while (last.nextSibling && last.nextSibling.hasAttribute && last.nextSibling.hasAttribute('listItemId')) last = last.nextSibling;
    return model.createPositionAfter(last);
}

/**
 * @param {import('ckeditor5').Editor} editor
 * @param {string} html
 * @param {{range: object|null, wasFocused: boolean}} target
 *   `range` is the (live) selection range captured when the import started.
 */
export function insertImportedHtml(editor, html, { range, wasFocused }) {
    const model = editor.model;
    const root = model.document.getRoot();
    const viewFragment = editor.data.processor.toView(html);
    const fragment = editor.data.toModel(viewFragment, '$clipboardHolder');
    if (!fragment.childCount) return;

    const useSelection = wasFocused && range && range.root === root;
    const selection = useSelection ? model.createSelection(range) : null;
    if (editor.isReadOnly || (selection && !model.canEditAt(selection))) throw new DocxImportError('TARGET_READONLY');

    model.change((writer) => {
        let position;
        let removeAfter = null;

        if (!selection) {
            // Never focused: append, replacing a trailing empty paragraph.
            const last = root.getChild(root.childCount - 1);
            if (last && last.is('element', 'paragraph') && last.isEmpty) {
                position = model.createPositionBefore(last);
                removeAfter = last;
            } else {
                position = model.createPositionAt(root, 'end');
            }
        } else {
            const selected = selection.getSelectedElement();
            if (selected) {
                position = model.createPositionAfter(selected);
            } else {
                if (!selection.isCollapsed) model.deleteContent(selection);
                const pos = selection.getFirstPosition();
                const block = topBlock(pos);
                if (!block || block.is('rootElement') || isLimit(block)) {
                    position = pos;
                } else if (block.hasAttribute('listItemId')) {
                    position = afterList(model, block);
                } else if (block.isEmpty) {
                    position = model.createPositionBefore(block);
                    removeAfter = block;
                } else if (pos.parent === block && pos.isAtStart) {
                    position = model.createPositionBefore(block);
                } else if (pos.parent === block && pos.isAtEnd) {
                    position = model.createPositionAfter(block);
                } else {
                    position = writer.split(pos, block.parent).position;
                }
            }
        }

        const inserted = model.insertContent(fragment, position);
        if (removeAfter && removeAfter.parent) writer.remove(removeAfter);
        writer.setSelection(inserted.end);
    });
    editor.editing.view.scrollToTheSelection();
}
